/**
 * MarketingSplashPage — the public-facing landing for wavedsp.ai.
 *
 * Minimal splash for closed-beta traffic. Public signup is intentionally
 * disabled — existing brand reps click the top-right "Brand Login" button
 * (or navigate directly to /login). VCs reach the gated /overview by
 * their personalized key.
 *
 * Design intent: matches the founder's reference screenshot exactly —
 * subtle concentric wave rings + soft teal/pink glows, WAVE BRAND DSP
 * logo lockup, headline "Redefining the Future of Influencer Commerce."
 * with "Influencer Commerce." in teal, and a single quiet subhead.
 */
import React from 'react';
import { Link } from 'react-router-dom';

const TEAL = '#00D4FF';

function SplashWaveMark({ size = 28 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
      <path d="M8 38 C 16 28, 24 28, 32 38 S 48 48, 56 38" stroke={TEAL} strokeWidth="4" fill="none" strokeLinecap="round" />
      <path d="M8 28 C 16 18, 24 18, 32 28 S 48 38, 56 28" stroke={TEAL} strokeWidth="3" fill="none" strokeLinecap="round" opacity="0.7" />
      <path d="M8 18 C 16 12, 24 12, 32 18 S 48 24, 56 18" stroke={TEAL} strokeWidth="2.5" fill="none" strokeLinecap="round" opacity="0.45" />
    </svg>
  );
}

// Background concentric rings — purely decorative SVG sitting behind the
// content. Anchored to the upper-left third so the headline sits inside
// the largest ring.
function BackgroundRings() {
  const rings = [220, 320, 440, 580, 740, 920];
  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden="true"
    >
      <g transform="translate(28%, 38%)" stroke="#0E2A33" fill="none">
        {rings.map((r) => (
          <circle key={r} cx={0} cy={0} r={r} strokeWidth="1" opacity="0.55" />
        ))}
      </g>
    </svg>
  );
}

export default function MarketingSplashPage() {
  return (
    <div
      data-testid="marketing-splash"
      className="relative min-h-screen w-full overflow-hidden bg-[#070708] text-white"
      style={{ fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif' }}
    >
      {/* Background atmosphere: concentric rings + soft glows. */}
      <BackgroundRings />
      <div
        className="absolute pointer-events-none"
        aria-hidden="true"
        style={{
          left: '18%',
          top: '24%',
          width: 380,
          height: 380,
          background: 'radial-gradient(circle, rgba(0,212,255,0.16) 0%, rgba(0,212,255,0) 65%)',
          filter: 'blur(8px)',
        }}
      />
      <div
        className="absolute pointer-events-none"
        aria-hidden="true"
        style={{
          right: '-6%',
          top: '38%',
          width: 520,
          height: 520,
          background: 'radial-gradient(circle, rgba(255,77,148,0.10) 0%, rgba(255,77,148,0) 65%)',
          filter: 'blur(10px)',
        }}
      />

      {/* Top nav — logo-less minimalist strip that houses the Brand Login
          entry point. Positioned absolute so the hero column layout is
          untouched; z-index sits above the background atmosphere. */}
      <header
        className="absolute top-0 inset-x-0 z-20 flex items-center justify-end px-6 sm:px-12 lg:px-24 pt-6 sm:pt-8"
        data-testid="splash-top-nav"
      >
        <Link
          to="/login"
          data-testid="brand-login-btn"
          className="group inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-2.5 text-sm font-semibold uppercase tracking-[0.14em] text-white/85 backdrop-blur-md transition-all duration-300 hover:border-[color:var(--teal)] hover:text-white hover:bg-white/10 hover:shadow-[0_0_24px_-6px_rgba(0,212,255,0.55)]"
          style={{ '--teal': TEAL }}
        >
          <span>Brand Login</span>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="transition-transform duration-300 group-hover:translate-x-0.5"
            aria-hidden="true"
          >
            <path d="M5 12h14" />
            <path d="m13 6 6 6-6 6" />
          </svg>
        </Link>
      </header>

      {/* Content column — left-anchored, generous padding. */}
      <main className="relative z-10 min-h-screen flex items-center px-6 sm:px-12 lg:px-24">
        <div className="max-w-[1080px] w-full">
          {/* Logo lockup */}
          <div
            className="flex items-center gap-3 mb-10"
            data-testid="splash-logo-lockup"
          >
            <SplashWaveMark size={30} />
            <span className="text-[26px] font-black tracking-tight leading-none">WAVE</span>
            <span className="text-[12px] uppercase tracking-[0.22em] text-gray-500 mt-[3px]">
              Brand DSP
            </span>
          </div>

          {/* Headline */}
          <h1
            className="font-black tracking-tight leading-[0.95]"
            style={{ fontSize: 'clamp(48px, 8.4vw, 124px)' }}
            data-testid="splash-headline"
          >
            <span className="block text-white">Redefining the</span>
            <span className="block text-white">Future of</span>
            <span className="block" style={{ color: TEAL }}>
              Influencer Commerce.
            </span>
          </h1>

          {/* Subhead */}
          <p
            className="mt-10 text-gray-400 max-w-[640px] leading-relaxed"
            style={{ fontSize: 'clamp(15px, 1.25vw, 19px)' }}
            data-testid="splash-subhead"
          >
            The ultimate demand-side platform that turns everyday creators
            into a synchronized, high-impact media channel.
          </p>
        </div>
      </main>
    </div>
  );
}
